// React-Screen CPosReceiptList ("Geparkte Bons" / "Rechnungs- und
// Bonkopien") - Strangler Phase 5 (entschalt).
// Runbook: tools/REACT-STRANGLER.md.
//
// ENTSCHALT am 23.8.2026, als letzter der grossen Listen-Screens. Er hat es
// in sich, deshalb der Reihe nach:
//
// 1. ZWEI MODI in einem Screen. "parked" (geparkte Bons laden) und "copies"
//    (Bonkopien drucken, stornieren, Kunde erfassen) unterscheiden sich in
//    einem Dutzend Sichtbarkeiten. Die Reihenfolge der style-Schluessel ist
//    dabei NICHT gleich: wo die Layout-XML schon eine visibility mitbringt,
//    ueberschreibt das Legacy sie an Ort und Stelle (sie bleibt vorne), wo
//    nicht, haengt sie hinten an. Beides steht hier so in der JSX, weil es
//    genau so im DOM landet.
// 2. Er baut in der Kopien-Ansicht einen ECHTEN CBonManager auf seinen
//    eigenen Handles: `new CBonManager(bonContainer, ..., inputField, ...)`.
//    Der greift auf `Bon.components.bon` (Datengitter) und
//    `Bon.components.bonSum` (CPre) zu und rendert dort hinein. Diese Namen
//    legt spec.mountLegacy an; die Gitter selbst entstehen schon in
//    prepare(), weil makeBodyList() noch dort die Liste fuellt.
// 3. Fremder Code greift von aussen zu: CEANManager schreibt die
//    Kundenkarte nach `components.inputField.input`, CMainPOS setzt dort
//    nach dem Zeichnen den Fokus. Dafuer gibt es verdrahteInputField().
//
// Vor dem Umbau sind am 23.8.2026 die Galerie-Masken beider Eintraege (16
// und 30) enger gezogen worden: vorher nahm '.CPosReceiptList table' ALLE
// Tabellen heraus - also auch Eingabefeld, Zehnerblock und Textzeile - und
// damit den halben Screen aus BEIDEN Netzen. Jetzt faellt nur noch der
// Inhalt der Datengitter heraus, der wirklich mit jedem Testlauf waechst.
// Die Baselines wurden dafuer neu aufgenommen, SOLANGE der Screen noch
// Legacy war.
import { defineReactScreen } from '../bridge.js';
import { CPushButton } from '../../components/pushbutton.js';
import { CImage } from '../../components/image.js';
import {
  Caption, TextLabel, PushPanel, NumPad, InputField, DataGrid, Pre,
  gitterVorbereiten, preVorbereiten, verdrahteInputField, reDrawUeberReact,
} from '../widgets.jsx';

const BILDKNOPF = (bild) => ({
  backgroundImage: `url(${bild})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center',
});

function CPosReceiptListScreen({ bridge }) {
  const t = bridge.t;
  const kopien = bridge.mode === 'copies';

  const knopf = (pfad, style, handler, params, text) => (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: { onMousedown: handler, onMouseout: true, onMouseup: true },
           attributes: params,
           proto: CPushButton.prototype,
         })}
         className="CPushButton" style={style}>
      <p {...bridge.el(`${pfad}:0`, 'CCaption')} className={'CCaption ' + (text || 'undefined')}
         dangerouslySetInnerHTML={{ __html: text || '' }} />
    </div>
  );

  return (
    <>
      {/* titleContainer */}
      <div {...bridge.el('0', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', left: '0px', top: '0px', height: '6.8%', width: '100%' }}>
        <Caption bridge={bridge} pfad="0:0"
                 text={kopien ? t('LABELS', 'BON_COPIES')
                   : (bridge.showPaydown ? t('LABELS', 'DOWNPAID_BONS') : t('LABELS', 'PARKED_BONS'))}
                 style={{ position: 'absolute', left: '5px', top: '7px',
                          fontSize: '3.8vh', fontWeight: 'bold' }} />
        {knopf('0:1', { position: 'absolute', right: '0.5%', top: '0px', width: '3%', height: '85%',
                        ...BILDKNOPF('lib/img/08_X.png') },
               bridge.cancel, { onMousedown_parameter_0: bridge.id })}
      </div>

      {/* listContainer mit der Bonliste */}
      <div {...bridge.el('1', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', overflow: 'hidden', borderStyle: 'solid',
                    borderWidth: '2px', height: '95%', width: '50%', top: '7%', left: '1%' }}>
        <DataGrid bridge={bridge} pfad="1:0" tableStyle={{ marginTop: '0px' }} />
      </div>

      {/* scrollBack (leerer Rahmen hinter den Pfeilen) */}
      <div {...bridge.el('2', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', overflow: 'hidden', borderStyle: 'solid',
                    borderWidth: '0px', height: '95%', width: '5%', top: '7%', left: '51%' }} />

      {knopf('3', { position: 'absolute', left: '51%', top: '7%', width: '4.6%', height: '8%',
                    ...BILDKNOPF('lib/img/bonedit/01scrollup.png') },
             bridge.scroll, { onMousedown_parameter_0: bridge.id, onMousedown_parameter_1: 'up' })}
      {knopf('4', { position: 'absolute', left: '51%', bottom: '0.5%', width: '4.6%', height: '8%',
                    ...BILDKNOPF('lib/img/bonedit/07scrolldown.png') },
             bridge.scroll, { onMousedown_parameter_0: bridge.id, onMousedown_parameter_1: 'down' })}

      {/* Zeigte bis 26.8.2026 das POSysseus-Logo, jetzt das Logo des
          Geschaefts. Das ELEMENT bleibt bestehen: es ist Kind 5, und ein Loch
          in der Kinderliste laesst removeMe() abstuerzen ("Cannot set
          properties of undefined (setting 'transmitted')") - genau so beim
          ersten Versuch passiert, sichtbar beim Ausparken eines Bons. */}
      <img {...bridge.el('5', 'CImage', { proto: CImage.prototype })}
           className="CImage" src="lib/img/customerlogo.gif"
           style={{ position: 'absolute', visibility: 'hidden' }} />

      {/* moneyButton: die XML bringt visibility:hidden MIT, "copies" setzt sie
          an Ort und Stelle auf visible - deshalb steht sie hier vorne. */}
      {knopf('6', { visibility: kopien ? 'visible' : 'hidden', position: 'absolute',
                    right: '27%', bottom: '25.5%', width: '14.3%', height: '6.5%' },
             kopien ? bridge.changeBonMoney : undefined,
             kopien ? { onMousedown_parameter_0: bridge.id } : {},
             t('BUTTONS', 'NEW_MONEY'))}

      {/* searchButton / resetButton: die XML hat KEINE visibility, "parked"
          haengt sie deshalb hinten an. */}
      {knopf('7', { position: 'absolute', right: '26.8%', bottom: '17.5%', width: '6.7%', height: '6.5%',
                    ...(kopien ? {} : { visibility: 'hidden' }) },
             bridge.openSearch, { onMousedown_parameter_0: bridge.id }, t('BUTTONS', 'NEW_SEARCH'))}
      {knopf('8', { position: 'absolute', right: '34.5%', bottom: '17.5%', width: '6.7%', height: '6.5%',
                    ...(kopien ? {} : { visibility: 'hidden' }) },
             bridge.resetSearch, { onMousedown_parameter_0: bridge.id }, t('BUTTONS', 'RESET_SEARCH'))}

      {/* functionButtons: 2x2, Belegung je Modus */}
      <PushPanel bridge={bridge} pfad="9" zeilen={2} spalten={2}
                 style={{ width: '14.5%', height: '15.5%', bottom: '1.5%', right: '27%',
                          visibility: 'visible' }}
                 knoepfe={bridge.funktionsKnoepfe || []} />

      {/* Zehnerblock und Eingabefeld: nur in der Kopien-Ansicht */}
      <NumPad bridge={bridge} pfad="10" typ="phoneAmount"
              style={kopien ? undefined : { visibility: 'hidden' }}
              panelStyle={kopien ? undefined : { visibility: 'hidden' }} />
      <InputField bridge={bridge} pfad="11"
                  style={{ position: 'absolute', height: '7%', width: '36.5%', bottom: '34%',
                           right: '6%', visibility: kopien ? 'visible' : 'hidden' }}
                  spaltenStyle={{ width: '93%' }}
                  inputStyle={{ borderStyle: 'solid', borderWidth: '2px', width: '93%' }}
                  inputProperties={{ type: 'Money' }} />

      {/* bonContainer: der ausgewaehlte Bon (Gitter + Summenzeile) */}
      <div {...bridge.el('12', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', visibility: kopien ? 'visible' : 'hidden',
                    overflow: 'hidden', borderStyle: 'solid', borderWidth: '2px',
                    height: '49%', width: '36%', top: '7%', right: '5.5%' }}>
        <DataGrid bridge={bridge} pfad="12:0" />
        <Pre bridge={bridge} pfad="12:1"
             style={{ borderTopStyle: 'double', borderTopWidth: '3px', fontSize: '1.1vw',
                      margin: '0px', padding: '2px' }} />
      </div>

      {/* scrollBon */}
      <div {...bridge.el('13', 'CFrame')} className="CFrame"
           style={{ position: 'absolute', overflow: 'hidden',
                    visibility: kopien ? 'visible' : 'hidden', borderStyle: 'solid',
                    borderWidth: '0px', height: '49%', width: '5%', top: '7%', right: '0.5%' }}>
        {knopf('13:0', { position: 'absolute', left: '0px', top: '0px', width: '90%', height: '15%',
                         ...BILDKNOPF('lib/img/bonedit/01scrollup.png') },
               kopien ? bridge.scrollBon : undefined,
               kopien ? { onMousedown_parameter_0: bridge.id, onMousedown_parameter_1: -30 } : {})}
        {knopf('13:1', { position: 'absolute', left: '0px', bottom: '0px', width: '90%', height: '15%',
                         ...BILDKNOPF('lib/img/bonedit/07scrolldown.png') },
               kopien ? bridge.scrollBon : undefined,
               kopien ? { onMousedown_parameter_0: bridge.id, onMousedown_parameter_1: 30 } : {})}
      </div>

      {/* suminfo: die Beschriftung wurde NIE gesetzt, deshalb steht dort der
          Vorgabewert von CTextLabel ("Text Label"). Unsichtbar, aber es
          steht so im DOM - also bleibt es. */}
      <TextLabel bridge={bridge} pfad="14" text="Text Label"
                 style={{ position: 'absolute', height: '5%', width: '21%', bottom: '25%',
                          right: '47%', visibility: 'hidden' }}
                 spaltenStyle={{ textAlign: 'right', paddingRight: '4px' }} />
    </>
  );
}

const legacy = {};


legacy.prepare = function() {

    var defret = $.Deferred();
    var PosReceiptListClass = this;

    // Beide Datengitter muss es VOR dem ersten Rendern geben: makeBodyList()
    // fuellt die Bonliste noch hier in prepare, und der CBonManager der
    // Kopien-Ansicht bekommt das Bon-Gitter im Konstruktor. Die
    // Voreinstellungen fuer Zeilen/Spalten stehen jetzt hier statt in
    // LYT_posReceiptList.xml.
    gitterVorbereiten(this, '1:0', {
        canSelect: true,
        row: { styles: { height: "5.5vh", borderBottomStyle: "solid", borderBottomWidth: "1px" } },
        column: {
            styles: {
                borderLeftStyle: "solid", borderLeftWidth: "1px",
                textAlign: "center", verticalAlign: "middle",
            },
        },
    });
    gitterVorbereiten(this, '12:0', {
        canSelect: true,
        row: {
            styles: {
                height: "4vh", borderBottomStyle: "solid", borderBottomWidth: "2px",
                fontSize: "1.1vw",
            },
        },
        column: { styles: { textAlign: "left", verticalAlign: "top" } },
    });
    // Die Rahmen und das Eingabefeld muessen ebenfalls schon existieren:
    // prepare() gibt sie dem CBonManager mit. Namen wie in der XML - und
    // zwar mit dem ENDGUELTIGEN Legacy-Namen, damit das Rendern dieselben
    // Handles wiederverwendet.
    this.handleVorbereiten('1', 'CFrame');
    this.handleVorbereiten('11', 'CInputField');
    this.handleVorbereiten('12', 'CFrame');
    // Die Summenzeile MIT Prototyp und eigenen clear/reValue/reDraw: der
    // CBonManager ruft alle drei. Der Prototyp gehoert zur Identitaet eines
    // Handles - stimmt er beim Rendern nicht ueberein, entsteht ein ZWEITES
    // Handle und der BonManager haelt das alte (live genau so passiert:
    // "this.SumGrid.clear is not a function").
    preVorbereiten(this, '12:1');
    this.nameComponent('1', 'listContainer');
    this.nameComponent('1:0', 'list');
    this.nameComponent('12', 'bonContainer');
    this.nameComponent('12:0', 'bon');
    this.nameComponent('12:1', 'bonSum');
    this.nameComponent('11', 'inputField');

    switch (this.mode) {
        case "parked":
            // Titel und alle Sichtbarkeiten entscheidet jetzt die JSX anhand
            // von bridge.mode/showPaydown - hier bleibt nur, was KEIN DOM ist.
            break;
        case "copies":
            this.Main.disableInputFocus();
            this.BonManager = new CBonManager(
                this.components.bonContainer,
                null,
                this.components.inputField,
                null,
                null,
                this.Main.userProps,
                this.Main);
            break;
    }

    // Die Verdrahtung der Knoepfe (cancel/search/reset/scroll) steht jetzt in
    // der JSX - hier bleibt nur, was Daten sind.
    this.components.listContainer.components.list.prepare();
    $.when(this.getBonsToShow()).then(function(val) {
        $.when(PosReceiptListClass.makeBodyList(val)).then(function() {
            PosReceiptListClass.funktionsKnoepfe = PosReceiptListClass.makeFunctionButtons();


            PosReceiptListClass.prepared = true;
            defret.resolve();
        });
    });
    return defret.promise();
}

legacy.scrollBon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);
    me.components.bonContainer.scroll(parameters[1]);
}

legacy.getBonsToShow = function() {
    /*privMan.enablePrivilege (privList);*/
    var PosReceiptListClass = this;
    var dfd = jQuery.Deferred();
    var bons = new Array();
    var nextBon = null;
    var nextTimeStamp = null;

    if (this.mode == "parked") {
        this.allBons = this.listedBons.length;

        for (var i = 0; bons.length < this.bonsPerScreen; i++) {
            if (typeof this.listedBons[(this.scrollScreen * this.bonsPerScreen) + i] !=
                "undefined") {
                nextBon = this.listedBons[(this.scrollScreen * this.bonsPerScreen) + i];
                bons[bons.length] = nextBon;
            } else
                break;
        }
        dfd.resolve(bons);
    } else {
        if (this.searchParameters) {
            if (this.searchParameters.length > 1) {
                var datePar = Array();
                datePar[0] = this.searchParameters[3] + 2000;
                datePar[1] = this.searchParameters[2] - 1;
                datePar[2] = this.searchParameters[1];
                datePar[3] = this.searchParameters[4];
                datePar[4] = this.searchParameters[5];

                var dParam = new Date(datePar[0], datePar[1], datePar[2], datePar[3], datePar[4]);

                var timestampParameter = (dParam / 1000).toFixed(0);
                timestampParameter = parseFloat(timestampParameter);

                var timeSearchCondition = "BON.End > " + (timestampParameter - 86400) +
                    " AND BON.End < " + (timestampParameter + 86400);
            } else {
                var bon = this.searchParameters[0];
                bon = parseFloat(bon);
                var bonSearchCondition = "POSBON.POSBON_ID > " + (bon - 500) +
                    " AND POSBON.POSBON_ID < " + (bon + 500);
            }
        } else {
            var limitCondition = " LIMIT 1000";
        }

        var sql = "SELECT POSBON_ID, End FROM POSBON INNER JOIN BON ON " +
            "POSBON.BON_ID = BON.BON_ID WHERE " +
            "(BON.Type = 'CLS' OR BON.Type = 'CAN' AND BON.Cancelled < -1)";

        if (bonSearchCondition)
            sql += " AND " + bonSearchCondition;
        if (timeSearchCondition)
            sql += " AND " + timeSearchCondition;

        sql += " ORDER BY POSBON_ID DESC";

        if (limitCondition)
            sql += limitCondition;

        $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
            //if (r.recordCount() >= 1)
            if (rows.length >= 1)
            //PosReceiptListClass.allBons = r.recordCount();
                PosReceiptListClass.allBons = rows.length;
            else if (PosReceiptListClass.searchParameters) {
                var text = POS.lang.getText("MESSAGES", "NO_SEARCH_RESULTS");
                POS.message.show(text, "warning", "OK");
                PosReceiptListClass.scrollScreen = 0;
                PosReceiptListClass.bestBon = null;
                PosReceiptListClass.searchParameters = null;
                //r.free();
                dfd.resolve(null);
                //return null;
                return;
            } else {
                POS.hourGlass.hide();
                var text = POS.lang.getText("MESSAGES", "NO_BONS_FOUND");
                POS.message.show(text, "warning", "OK", PosReceiptListClass, PosReceiptListClass.removeComp);
                PosReceiptListClass.scrollScreen = 0;
                PosReceiptListClass.bestBon = null;
                PosReceiptListClass.searchParameters = null;
                //r.free();
                dfd.resolve(null);
                return;
                //return null;
            }

            if (PosReceiptListClass.searchParameters && !PosReceiptListClass.bestBon) {
                PosReceiptListClass.getBestHit(rows);
                //r.moveFirst();
            }

            //        sdump("bestBon: ", PosReceiptListClass.bestBon);
            //        sdump("scrollScreen: ", PosReceiptListClass.scrollScreen);
            var j = 0;

            for (i = 0; i < PosReceiptListClass.scrollScreen * PosReceiptListClass.bonsPerScreen; i++) {
                if (j < rows.length) {
                    //r.moveNext();
                    j++;
                } else
                    break;
            }

            for (i = 0; bons.length < PosReceiptListClass.bonsPerScreen; i++) {
                //nextBon = r.getColumnByName("POSBON_ID");
                nextBon = rows[j].POSBON_ID;

                bons[bons.length] = nextBon;
                if (j < rows.length - 1) {
                    //r.moveNext();
                    j++;
                } else
                    break;
            }
            //r.free();
            dfd.resolve(bons);
        });
    }
    return dfd.promise();
    //return bons;
}

legacy.getBestHit = function(r) {
    for (var i = 0; i < r.length; i++) {
        //switch (this.checkParameters (r.getColumnByName("POSBON_ID"), r.getColumnByName("End")))
        switch (this.checkParameters(r[i].POSBON_ID, r[i].End)) {
            case 0:
                this.scrollScreen = Math.floor((i) / this.bonsPerScreen);
                return;
            case 1:
                this.scrollScreen = Math.floor((i - 1) / this.bonsPerScreen);
                return;
            case -1:
                this.scrollScreen = Math.floor((i) / this.bonsPerScreen);
                break;
        }

        //r.moveNext();
    }
}

legacy.checkParameters = function(bonId, timestamp) {
    if (this.searchParameters[0] != -1) {
        if (bonId == this.searchParameters[0]) {
            this.bestBon = bonId;
            return 0;
        } else {
            if (Math.abs(bonId - this.searchParameters[0]) <
                Math.abs(this.bestBon - this.searchParameters[0])) {
                this.bestBon = bonId;
                return -1;
            } else {
                if (this.bestBon)
                    return 1;
                else
                    return -1;
            }
        }
    }

    var datePar = Array();

    datePar[0] = this.searchParameters[3] + 2000;
    datePar[1] = this.searchParameters[2] - 1;
    datePar[2] = this.searchParameters[1];
    datePar[3] = this.searchParameters[4];
    datePar[4] = this.searchParameters[5];

    var dParam = new Date(datePar[0], datePar[1], datePar[2], datePar[3], datePar[4]);

    var timestampParameter = (dParam / 1000).toFixed(0);

    if (timestamp == timestampParameter) {
        this.bestBon = bonId;
        this.bestTimestamp = timestamp;
        return 0;
    } else {
        //        sdump (bonId, Math.abs(timestamp - timestampParameter), Math.abs(this.bestTimestamp - timestampParameter));
        if (Math.abs(timestamp - timestampParameter) <
            Math.abs(this.bestTimestamp - timestampParameter)) {
            this.bestBon = bonId;
            this.bestTimestamp = timestamp;
            return -1;
        } else {
            if (this.bestBon)
                return 1;
            else
                return -1;
        }
    }
}

legacy.scroll = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    switch (parameters[1]) {
        case "up":
            if (me.scrollScreen > 0) {
                me.scrollScreen--;
                me.reDrawList();
            } else
                return;
            break;
        case "down":
            if (me.canScrollDown()) {
                me.scrollScreen++;
                me.reDrawList();
            } else
                return;
            break;
    }
}

legacy.canScrollDown = function() {
    var nextBon = (this.scrollScreen + 1) * this.bonsPerScreen;

    if (nextBon >= this.allBons)
        return false;
    else
        return true;
}

legacy.makeFunctionButtons = function() {
    // Seit 23.8.2026 reine DATEN statt CPushButton-Objekten - das Raster
    // rendert PushPanel. Beschriftungen und Sichtbarkeiten stammen aus
    // LYT_bonParkedListButtons.xml bzw. LYT_bonCopiesListButtons.xml, die
    // Verdrahtung aus dem Legacy-switch darunter.
    var t = function(key) { return POS.lang.getText("BUTTONS", key); };

    if (this.mode == "parked") {
        return [
            this.showPaydown
                ? { text: t("BON_CANCEL"), param0: this.id, style: { visibility: "hidden" } }
                : { text: t("BON_CANCEL"), handler: this.removeParkedBon, param0: this.id },
            { text: t("LOAD_BON"), handler: this.loadParkedBon, param0: this.id },
        ];
    }

    return [
        { text: t("PRINT_BILL_BON_COPY"), handler: this.printCopy, param0: this.id, param1: "billcopy" },
        // Dieser Knopf ist in der XML unsichtbar gesetzt und bekommt deshalb
        // auch keinen Handler.
        { text: t("PRINT_BON"), param0: this.id, style: { visibility: "hidden" } },
        { text: t("ADD_CUSTOMER_TO_BON"), handler: this.addCustomer, param0: this.id },
        { text: t("BON_CANCEL"), handler: this.preCancelBon, param0: this.id },
    ];
}
legacy.addCustomer = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    /*privMan.enablePrivilege (privList);*/

    var bonGrid = me.components.listContainer.components.list;
    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }
    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;
    $.when(Trans.getBonIdFromPosBon(selectedBon)).then(function(bonId) {
        $.when(Trans.getCustomerIdFromBon(bonId)).then(function(dbCust) {
            if (dbCust) {
                var text = POS.lang.getText("MESSAGES", "CUSTOMER_ALREADY_REGISTERED");
                POS.message.show(text, "warning", "OK");
                return;
            }

            var cardNumber = me.components.inputField.input.properties.attributes.value;
            if (cardNumber == "") {
                var text = POS.lang.getText("MESSAGES", "PLEASE_SCAN_CUSTOMER_CARD");
                POS.message.show(text, "warning", "OK");
                return;
            }

            var sql = "SELECT CUST_ID, FirstName, Surname FROM CUST WHERE Barcode = " + cardNumber;
            $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
                //if (! r.recordCount())
                if (rows.length < 1) {
                    var text = POS.lang.getText("MESSAGES", "CUSTOMER_NOT_FOUND");
                    POS.message.show(text, "warning", "OK");
                    return;
                }

                /*me.customerId = parseInt(r.getColumnByName("CUST_ID"));
                var firstName = r.getColumnByName("FirstName");
                var surname = r.getColumnByName("Surname");*/
                me.customerId = parseInt(rows[0].CUST_ID);
                var firstName = rows[0].FirstName;
                var surname = rows[0].Surname;
                var custName = firstName + ' ' + surname

                me.bonForCustomer = bonId;

                var text = POS.lang.getText("MESSAGES", "CONF_ADD_CUSTOMER").replace("%X%", custName);
                POS.message.show(text, "question", "YESNO", me, me.doAddCustomer);
            });
        });
    });
}

legacy.doAddCustomer = function(me, result) {
    var custId = me.customerId;
    var bonId = me.bonForCustomer;
    me.customerId = null;
    me.bonForCustomer = null;

    if (result != "YES")
        return;

    Trans.updateCustomerToBon(bonId, custId);

    var text = POS.lang.getText("MESSAGES", "CUSTOMER_SUCCESFULLY_REGISTERED");
    POS.message.show(text, "info", "OK");
}

legacy.testCancel = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    /*privMan.enablePrivilege (privList);*/

    var selectedBon = parameters[1];

    var sql = "SELECT BON.BON_ID, BON.Cancelled, BON.End FROM POSBON INNER JOIN BON " +
        "ON BON.BON_ID = POSBON.BON_ID WHERE POSBON_ID = " + selectedBon;
    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        /*var bonId = parseInt(r.getColumnByName("BON.BON_ID"));
        var cancelled = r.getColumnByName("BON.Cancelled");
        var endTS = parseFloat(r.getColumnByName("BON.End"));*/
        var bonId = parseInt(rows[0].BON_ID);
        var cancelled = rows[0].Cancelled;
        var endTS = parseFloat(rows[0].End);

        if (bonId != me.BonManager.currentBonId) {
            me.BonManager.reset();
            var r3 = me.BonManager.loadFromId(bonId, true);
        } else {
            r3 = 0;
        }
        $.when(r3).then(function() {
            //r.free();

            var d = new Date();
            var nowTS = (d / 1000).toFixed(0);

            if (cancelled != "0" || nowTS - endTS > 3600 * 14) // only allowed for the last 14 hours!
            {
                me.components.functionButtons.children[3].setEnabled(false);
                return;
            }

            sql = "SELECT * FROM BONE WHERE BON_ID = " + bonId + " AND Cancelled = 0 AND " +
                "((Type = 'PAY' AND Text1 = 'PPU') OR Type = 'CDS')";

            $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
                if (rows.length > 0) {
                    me.components.functionButtons.children[3].setEnabled(false);
                    return;
                }

                me.components.functionButtons.children[3].setEnabled(true);
                //r.free();
            });
        });
    });
}

legacy.preCancelBon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    if (me.components.listContainer.components.list.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    var text = POS.lang.getText("MESSAGES", "CONF_CANCEL_CLOSED_BON");
    POS.message.show(text, "question", "YESNO", me, me.cancelBon);

}

legacy.cancelBon = function(me, result) {
    if (result != "YES")
        return;

    /*privMan.enablePrivilege (privList);*/

    var bonGrid = me.components.listContainer.components.list;
    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;
    $.when(Trans.getBonIdFromPosBon(selectedBon)).then(function(bonId) {
        $.when(Trans.getPPCProduct()).then(function(val) {
            var sql = "SELECT SUM(Amount) AS Sum FROM BONE WHERE BON_ID = " +
                bonId + " AND Cancelled = 0 AND Type = 'POS' AND Foreign_ID = " + val;
            $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {

                //var sumPPCProds = r.getColumnByName("Sum");
                //r.free();
                var sumPPCProds = rows[0].Sum;

                if (sumPPCProds > 0) {
                    POS.timeout.register("testPPCCancellationPing", me.testPPCCancellationPing, me, 8 * 4, 1); // 8 seconds
                    var PPC = new CPrepaidCardPayment();
                    PPC.checkBonCancellation(me.Main.UID, POS.POSId, bonId);
                    POS.hourGlass.show(POS.lang.getText("MESSAGES", "PLEASE_WAIT"), null, null);
                } else
                    me.doCancelBon(me, "YES");
            });

        });
    });
}

legacy.testPPCCancellationPing = function(me) {
    if (POS.sessions.getSessionComponent() == me.Main) {
        POS.hourGlass.hide();
        var text = POS.lang.getText("MESSAGES", "PING_TOO_LONG");
        POS.message.show(text, "warning", "OK");
    }
}

legacy.checkPPCCancellationAnswer = function(ok) {
    POS.hourGlass.hide();
    POS.timeout.unregister("testPPCCancellationPing");
    var me = this;
    var defret = jQuery.Deferred();
    switch (ok) {
        case "NOK":
            var text = POS.lang.getText("MESSAGES", "PPC_CANCELLATION_NOT_POSSIBLE");
            POS.message.show(text, "warning", "OK");
            defret.resolve();
            break;
        case "OK":
            $.when(me.doCancelBon(me, "YES")).done(function() {
                defret.resolve();
            });
            break;
    }
    return defret.promise();
}

legacy.doCancelBon = function(me, result) {
    if (result != "YES")
        return 0;
    var defret = jQuery.Deferred();
    var bonGrid = me.components.listContainer.components.list;
    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;
    $.when(Trans.getBonIdFromPosBon(selectedBon)).then(function(bonId) {
        me.removeComp(me);
        me.Main.cancelClosedBon(bonId);
        defret.resolve();
    });
    return defret.promise();
}

legacy.printDelivery = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    /*privMan.enablePrivilege (privList);*/

    var bonGrid = me.components.listContainer.components.list;

    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;

    var sql = "UPDATE BON SET U_ID = " + me.Main.userProps.Login + ", UName = '" +
        me.Main.userProps.Name + "', Exported = 0 WHERE BON_ID = " + selectedBon;

    $.when(POS.SQLiteCon.execute(sql)).then(function() {
        sql = "SELECT Text1, Qty FROM BONE WHERE BON_ID = " + selectedBon +
            " AND Type = 'POS' AND Cancelled = 0";
        $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
            var parameters = new Array("products", "quantities");
            parameters.products = new Array();
            parameters.quantities = new Array();

            for (var i = 0; i < rows.length; i++) {
                /*parameters.products[i] = r.getColumnByName("Text1");
                parameters.quantities[i] = parseFloat(r.getColumnByName("Qty"));*/
                parameters.products[i] = rows[0].Text1;
                parameters.quantities[i] = parseFloat(rows[0].Qty);
                // r.moveNext();
            }

            //r.free();

            me.printDeliveryBill(parameters, selectedBon);
            me.reDrawList();
        });
    });
}

// (legacy.bonComplain ist am 23.8.2026 geloescht worden. Sie war die
// EINZIGE Konstruktionsstelle von CBonDisjoint - und haengte an keinem
// Knopf: weder LYT_bonCopiesListButtons.xml noch
// LYT_bonParkedListButtons.xml verdrahten sie, live nachgezaehlt hat die
// Bonkopien-Liste 26 Knoepfe und keiner davon fuehrt dorthin. Der erreichbare
// Reklamations-Weg laeuft ueber CTransactionShowMode und die
// Warengruppen-Auswahl, nicht ueber diesen Screen. Der Originalcode steht in
// der Git-Historie.)
legacy.openSearch = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var cmp = me.Main.addChild(CBonSearch);
    cmp.parentComp = me;
    cmp.draw();
}

legacy.resetSearch = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    me.scrollScreen = 0;
    me.searchParameters = null;
    me.bestBon = null;
    me.bestTimestamp = null;
    me.allBons = 0;

    me.reDrawList();
}

legacy.buildNewList = function(me, result) {

    $.when(me.buildArrayBons(true)).then(function(val) {
        if (!val)
        //if (!me.buildArrayBons(true))
            me.removeMe();
        else
            me.reDrawList();
    });
}

legacy.reDrawList = function() {

    var PosReceiptListClass = this;
    var defret = $.Deferred();
    $.when(this.getBonsToShow()).then(function(val) {
        $.when(PosReceiptListClass.makeBodyList(val)).then(function() {
            //PosReceiptListClass.makeBodyList(PosReceiptListClass.getBonsToShow());
            PosReceiptListClass.components.listContainer.reDraw();
            defret.resolve();
        });

    });
    return defret.promise();
}

legacy.loadParkedBon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var bonGrid = me.components.listContainer.components.list;

    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;

    $.when(me.isBonAlreadyClosed(selectedBon)).then(function(val) {
        if (val && !me.showPaydown) //val = true or false
        {
            var text = POS.lang.getText("MESSAGES", "BON_ALREADY_CLOSED");
            POS.message.show(text, "warning", "OK", me, me.buildNewList);
            return;
        }

        if (me.isBonInUse(selectedBon)) {
            var text = POS.lang.getText("MESSAGES", "BON_IN_USE");
            POS.message.show(text, "warning", "");
            return;
        }

        if (!me.showPaydown) {
            me.Main.BonManager.reset();
            var r3 = me.Main.BonManager.loadFromId(selectedBon);
        } else {
            r3 = me.Main.BonManager.recoverPaydownBon(selectedBon);
            //r3 = 0;
        }

        $.when(r3).then(function() {
            if (me.Main.BonManager.currentCustomerId)
                me.Main.InfoIcons.highlightIcon("customer");

            me.removeComp(me);
        });
    });
}

legacy.showWarningMessage = function() {
    var text = POS.lang.getText("MESSAGES", "NO_POSITION_SELECTED");
    POS.message.show(text, "warning", "");
    return;
}

legacy.isBonAlreadyClosed = function(bonId) {
    /*privMan.enablePrivilege (privList);*/
    var dfd = jQuery.Deferred();
    var sql = "SELECT Type FROM BON WHERE BON_ID = " + bonId;

    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        //if (! r.recordCount())
        if (rows.length < 1) {
            // r.free();
            //return true;
            dfd.resolve(true);
            return;
        }

        var type = rows[0].Type;
        //r.free();

        if (type == "NEW") {
            dfd.resolve(false);
            return;
            //return false;
        } else {
            dfd.resolve(true);
            return;
        };
        //return true;
    });
    return dfd.promise();
}

legacy.isBonInUse = function(bonId) {
    for (var j in POS.sessions.openSessions)
        if (j != 0)
            if (POS.sessions.openSessions[j].BonManager.currentBonId == bonId)
                return true;

    return false;
}

legacy.removeParkedBon = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var bonGrid = me.components.listContainer.components.list;

    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    me.selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;
    $.when(me.isBonAlreadyClosed(me.selectedBon)).then(function(val) {
        if (val && !me.showPaydown) //val = true or false
        {
            var text = POS.lang.getText("MESSAGES", "BON_ALREADY_CLOSED_CANCEL");
            POS.message.show(text, "warning", "OK", me, me.buildNewList);
            return;
        }

        if (me.isBonInUse(me.selectedBon)) {
            var text = POS.lang.getText("MESSAGES", "BON_IN_USE_CANCEL");
            POS.message.show(text, "warning", "");
            return;
        }

        me.cancelCmp = me.addChild(CCancelBonMode);
        me.cancelCmp.Main = me.Main;
        me.cancelCmp.BonListComp = me;
        me.cancelCmp.bonFromList = me.selectedBon;
        me.cancelCmp.draw();
    });
}

legacy.removeAll = function() {

    var me = this;
    var defret = jQuery.Deferred();
    //for (var i = 0; i < this.listedBons.length; i++) {
    var iterator = function it(i) {
        if (i < me.listedBons.length) {
            $.when(me.printCancelledBon(me.listedBons[i])).done(function() {
                $.when(me.updateBonDB(me.listedBons[i], "ACP")).done(function() {
                    iterator(i + 1);
                });
            });
        } else {
            defret.resolve();
        }
    }

    iterator(0);

    return defret.promise();
}

legacy.printCancelledBon = function(selectedBon) {
    var cls = this.Main.BonManager;
    var defret = jQuery.Deferred();
    $.when(cls.loadFromId(selectedBon)).done(function() {
        cls.DateTime = new Date();
        cls.printMode = "cancelprotocol";
        cls.cancelReasonText = "";
        $.when(cls.printBon()).done(function() {

            Display.addLine();
            Display.addElement("* STORNO *", 9, 2, 0, 19);
            Display.addLine();
            Display.addElement("", 9, 2, 0, 19);
            Display.out();

            window.setTimeout("POS.sessions.openSessions[POS.sessions.currentSession].BonManager.initDisplay()", 5000);
            defret.resolve();
        });
    });
    return defret.promise();
}

legacy.printCopy = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var bonGrid = me.components.listContainer.components.list;

    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    var selectedBon = bonGrid.rows[bonGrid.selectedRowIndex].row.children[0].properties.value;

    /*privMan.enablePrivilege (privList);*/

    var sql = "SELECT BON_ID FROM POSBON WHERE POSBON_ID = " + selectedBon;
    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        //var bonIdDB = r.getColumnByName("BON_ID");
        var bonIdDB = rows[0].BON_ID;
        //r.free();

        var cls = new CBonManager();
        cls.Main = me.Main;
        cls.userProps = me.Main.userProps;
        $.when(me.isOriginalBill(bonIdDB)).then(function(val) {

            if (parameters[1] == "billcopy" && val)
                parameters[1] = "bill";
            cls.printMode = parameters[1];
            $.when(cls.loadFromId(bonIdDB)).then(function() {
                cls.currentBonPOSId = selectedBon;
             //   $.when(cls.updateInstantRebate()).then(function() {
                    cls.isReclamationBon = false;
                    for (var pos in cls.currentBon)
                        if (cls.currentBon[pos].type == "REC" && !cls.currentBon[pos].cancelled)
                            cls.isReclamationBon = true;

                    var sql = "SELECT Type, Cancelled FROM BON WHERE BON_ID = " + bonIdDB;
                    $.when(POS.SQLiteCon.execute(sql)).then(function(rows2) {
                        //var typeDB = r.getColumnByName("Type");
                        var typeDB = rows2[0].Type;
                        var cancelledDB = parseFloat(rows2[0].Cancelled);
                        //r.free();

                        if (typeDB == "CAN") {
                            cls.printMode = "cancellation";
                            cls.cancelReasonText = Trans.getCancelText(cancelledDB);
                        }

                        cls.printBon();
                    });
                });
           // });
        });
    });
}

legacy.isOriginalBill = function(bonId) {
    /*privMan.enablePrivilege (privList);*/
    var dfd = jQuery.Deferred();
    var sql = "SELECT Closed FROM BON WHERE BON_ID = " + bonId + " AND POS_ID = " + POS.POSId;

    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        //var closed = r.getColumnByName("Closed");
        var closed = rows[0].Closed;
        // r.free();

        if (closed == -1) {
            //return true;
            dfd.resolve(true);
            return;
        } else {
            //return false;
            dfd.resolve(false);
            return;
        }
    });
    return dfd.promise();
}

legacy.updateBonDB = function(selectedBon, mode) {
    /*privMan.enablePrivilege (privList);*/

    var d = new Date();

    var sql = "UPDATE BON SET Closed = -1, End = " + (d / 1000).toFixed(0) +
        ", Type = '" + mode + "', Cancelled = -1, Exported = 0 WHERE BON_ID = " +
        selectedBon;
    return POS.SQLiteCon.execute(sql);
}

legacy.buildArrayBons = function(noMessage, oneUserMode) {
    /*privMan.enablePrivilege (privList);*/
    var PosReceiptListClass = this;
    var dfd = jQuery.Deferred();
    var sql = "SELECT BON_ID FROM BON WHERE Type = ";

    switch (this.mode) {
        case "parked":
            if (this.showPaydown)
                sql += "'CLS' AND Closed = -3";
            else
                sql += "'NEW' AND End = 0";
            break;
    }

    if (oneUserMode)
        sql += "AND U_ID = " + this.Main.UID;

    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        if (rows.length < 1) {
            //r.free();
            if (noMessage) {
                dfd.resolve(false);
                return;
            }
            //return false;
            switch (PosReceiptListClass.mode) {
                case "parked":
                    if (!PosReceiptListClass.showPaydown)
                        var text = POS.lang.getText("MESSAGES", "NO_PARKED_BONS");
                    else
                        text = POS.lang.getText("MESSAGES", "NO_PAYDOWN_BONS");
                    break;
            }
            POS.message.show(text, "warning", "OK");
            dfd.resolve(false);
            return;
            //return false;
        }

        PosReceiptListClass.listedBons = new Array();

        for (var i = 0; i < rows.length; i++) {
            PosReceiptListClass.listedBons[rows.length - i - 1] = rows[i].BON_ID;
            //r.moveNext();
        }

        //r.free();
        dfd.resolve(true);
    });
    //return true;
    return dfd.promise();
}

legacy.makeBodyList = function(bonsToShow) {
    var PosReceiptListClass = this;
    var bonGrid = this.components.listContainer.components.list;
    bonGrid.clear();

    var row = bonGrid.addRowWithColumns(this.makeHeadList());
    row.row.properties.styles.height = "35px";
    row.selectable = false;
    var def = $.Deferred();
    var defret = $.Deferred();
    if (!bonsToShow) {

        $.when(this.getBonsToShow()).then(function(bTS) {
            bonsToShow = PosReceiptListClass.getBonsToShow();
            def.resolve();
        });
    } else {
        def.resolve();
    }

    $.when(def).then(function() {
        var maxItem;

        if (bonsToShow == null) {
            defret.resolve();
            return;
        }
        var iterator = function it(j) {
            //for (var j = 0; j < bonsToShow.length; j++) {

            if (j < bonsToShow.length) {

                $.when(PosReceiptListClass.getBonInfo(bonsToShow[j])).then(function(bonInfo) {
                    //var bonInfo = PosReceiptListClass.getBonInfo(bonsToShow[j]);

                    var columns = new Array();

                    for (var i = 0; i < 4; i++)
                        var n = columns.push(new CColumn(0, 0));

                    columns[0].properties.styles.width = "8.79%";
                    columns[1].properties.styles.width = "16.60%";
                    columns[2].properties.styles.width = "10.74%";
                    columns[3].properties.styles.width = "16.60%";

                    columns[0].properties.value = String(bonsToShow[j]);

                    if (PosReceiptListClass.mode == "parked")
                    //            columns[0].properties.styles.color = "#FFFFFF";
                        columns[0].properties.styles.visibility = "hidden";

                    var d = new Date(bonInfo[0] * 1000);

                    var dateToShow = d.getDate() + "." + (d.getMonth() + 1) + "." +
                        (d.getYear() + 1900) + " - " + d.toLocaleTimeString();

                    columns[1].properties.value = String(dateToShow);
                    columns[2].properties.value = String(bonInfo[1] + " &euro;");
                    columns[3].properties.value = String(bonInfo[2]);

                    if (PosReceiptListClass.mode == "copies") {
                        for (var z = 0; z < columns.length; z++) {
                            columns[z].properties.events.onMousedown = PosReceiptListClass.testCancel;
                            columns[z].properties.attributes.onMousedown_parameter_0 = PosReceiptListClass.id;
                            columns[z].properties.attributes.onMousedown_parameter_1 = bonsToShow[j];
                            columns[z].properties.attributes.onMousedown_parameter_2 = "unlock";
                        }
                    }

                    bonGrid.addRowWithColumns(columns);

                    if (bonsToShow[j] == PosReceiptListClass.bestBon) {
                        bonGrid.selectedRowIndex = j + 1;
                        bonGrid.rows[j + 1].row.properties.styles.backgroundColor = "#D9E2F0";
                    }
                    iterator(j + 1);
                    return;
                });
            } else {
                defret.resolve();
                return;
            }
        }
        iterator(0);
    });
    return defret.promise();
}

legacy.getBonInfo = function(idBon) {
    /*privMan.enablePrivilege (privList);*/
    var PosReceiptListClass = this;
    var dfd = jQuery.Deferred();
    if (this.mode == "parked")
        var sql = "SELECT Start, UName FROM BON WHERE BON_ID = " + idBon;
    else {
        sql = "SELECT BON.BON_ID, End, UName FROM POSBON INNER JOIN BON ON " +
            "POSBON.BON_ID = BON.BON_ID WHERE POSBON.POSBON_ID = " + idBon;
    }

    $.when(POS.SQLiteCon.execute(sql)).then(function(rows) {
        if (rows.length != 1) {
            //r.free();
            dfd.resolve(0);
            return;
            //return 0;
        }

        if (PosReceiptListClass.mode != "parked")
            idBon = rows[0].BON_ID;

        $.when(Trans.getTotalCashed(idBon)).then(function(tot) {
            window.sumTotal = float2StringD2(tot);
            var parameters = new Array();

            if (PosReceiptListClass.mode == "parked")
                parameters[0] = rows[0].Start;
            else
                parameters[0] = rows[0].End;
            parameters[1] = sumTotal;
            parameters[2] = rows[0].UName;

            //r.free();
            dfd.resolve(parameters);
        });
    });
    //return parameters;
    return dfd.promise();
}

legacy.makeHeadList = function() {
    var columns = new Array();

    for (var i = 0; i < 4; i++) {
        var n = columns.push(new CColumn(0, 0));
        columns[i].properties.styles.backgroundColor = "#94A5D6";
        columns[i].properties.styles.fontWeight = "bold";
        columns[i].properties.styles.color = "#FFFFFF";
    }

    columns[0].properties.styles.width = "10.74%";

    columns[1].properties.styles.width = "16.6%";

    columns[2].properties.styles.width = "10.74%";

    columns[3].properties.styles.width = "10.74%";

    columns[0].properties.value = String("Bonnummer");
    columns[1].properties.value = String("Datum - Zeit");
    columns[2].properties.value = String("Summe");
    columns[3].properties.value = String("Benutzer");

    return columns;
}

legacy.cancel = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    me.removeComp(me);
}

legacy.removeComp = function(me) {
    log.add(4, "Info", "Back to MainPOS from bons list", "");
    me.Main.enableInputFocus();
    me.removeMe();
}

legacy.printDeliveryBill = function(parameters, bonId) {
    window.Printer = new CPrinter();

    $.when(Printer.head()).done(function() {
        Printer.addLine("A", 1, false, 0);
        window.text = POS.lang.getText("LABELS", "DELIVERY_BILL");
        Printer.addElement(text, 20, 2, true, 0, 41);

        Printer.addDivisionLine();

        var ColLimits = new Array();
        ColLimits[0] = 34;
        ColLimits[1] = 42;

        Printer.addLine("A", 1, false, 0);
        Printer.addElement(POS.lang.getText("LABELS", "PRODUCT"), 0, 0, false, 0,
            ColLimits[0] - 1);
        Printer.addElement(POS.lang.getText("LABELS", "AMOUNT"), ColLimits[1] - 1,
            1, false, ColLimits[0], ColLimits[1] - 1);

        Printer.addLine("B", 1, false, 2);

        for (var i = 0; i < parameters.products.length; i++) {
            Printer.addLine("A", 1, false, 0);
            Printer.addElement(parameters.products[i], 0, 0, false, 0, ColLimits[0] - 1);
            window.text = parameters.quantities[i];
            Printer.addElement(text, ColLimits[1] - 1, 1, false, ColLimits[0],
                ColLimits[1] - 1);
        }

        Printer.addDivisionLine();

        Printer.addLine("B", 1, false, 0);
        window.text = POS.lang.getText("LABELS", "CASHBOX") + " | " +
            POS.lang.getText("LABELS", "BON_NUMBER") + " | " +
            POS.lang.getText("LABELS", "USER") + " |   " +
            POS.lang.getText("LABELS", "DATE") + "    | " +
            POS.lang.getText("LABELS", "TIME");
        Printer.addElement(text, 4, 0, false, 4, 55);

        Printer.addLine("B", 1, false, 0);

        var now = new Date();

        window.text = expandInteger(POS.POSId, 5) + " | " + expandInteger(bonId, 9) + " | ";
        window.text += expandInteger(POS.sessions.getSessionComponent().userProps.Login, 8) + " | " +
            expandInteger(now.getDate(), 2) +
            "." + expandInteger(now.getMonth() + 1, 2) +
            "." + now.getFullYear() + " |  " + expandInteger(now.getHours(), 2) +
            ":" + expandInteger(now.getMinutes(), 2);
        Printer.addElement(text, 4, 0, false, 4, 55);

        Printer.print();
    });
}

legacy.changeBonMoney = function(component, parameters) {
    var me = POS.getComponentById(parameters[0]);

    var bonGrid = me.components.listContainer.components.list;

    if (bonGrid.selectedRowIndex == -1) {
        me.showWarningMessage();
        return;
    }

    var payAmount = me.components.inputField.input.properties.attributes.value;

    if (payAmount == "") {
        var text = POS.lang.getText("MESSAGES", "NO_AMOUNT");
        POS.message.show(text, "warning", "OK");
        return;
    }

    if (payAmount[payAmount.length - 1] == ",")
        payAmount = payAmount.replace(",", "");

    payAmount = parseFloat(payAmount.replace(",", "."));

    if (payAmount > 10000) {
        var text = POS.lang.getText("MESSAGES", "TOO_MUCH_CUSTOMER_AMOUNT");
        POS.message.show(text, "warning", "OK");
        return;
    }

    $.when(Trans.getLastUserPayment(me.Main.userProps.Login)).then(function(lastValidBonId) {
        if (lastValidBonId != me.BonManager.currentBonId) {
            var text = POS.lang.getText("MESSAGES", "ONLY_LAST_BON");
            POS.message.show(text, "warning", "OK");
            return;
        }

        $.when(Trans.getLastPaymentDetails(me.BonManager.currentBonId)).then(function(payDetails) {
            me.payDetails = payDetails;
            if (me.payDetails["type"] != "BAR") {
                var text = POS.lang.getText("MESSAGES", "ONLY_CASH_PAYMENT");
                POS.message.show(text, "warning", "OK");
                return;
            }

            var d = new Date();
            var nowTS = (d / 1000).toFixed(0);

            if (nowTS - parseFloat(me.payDetails["ts"]) > 1800) {
                var text = POS.lang.getText("MESSAGES", "ONLY_HALF_HOUR_BONS");
                POS.message.show(text, "warning", "OK");
                return;
            }

            var totalAmount = me.payDetails["payAmount"] - me.payDetails["retAmount"];

            if (payAmount - totalAmount < 0) {
                var text = POS.lang.getText("MESSAGES", "NEW_AMOUNT_NOT_VALID");
                POS.message.show(text, "warning", "OK");
                return;
            }

            if (me.payDetails["changed"] == "CHD") {
                var text = POS.lang.getText("MESSAGES", "AMOUNT_ALREADY_CHANGED");
                POS.message.show(text, "warning", "OK");
                return;
            }

            var text = POS.lang.getText("MESSAGES", "CONF_CHANGE_BON_AMOUNT");

            POS.message.show(text, "warning", "YESNO", me, me.doChangeBonMoney);

            me.payAmount = payAmount;
            me.retAmount = payAmount - totalAmount;
        });
    });
}

legacy.doChangeBonMoney = function(me, result) {
    if (result != "YES") {
        me.resetChangeBonMoney();
        return 0;
    }
    var defret = $.Deferred();
    $.when(Trans.updatePaymentEntry(me.payDetails["boneId"], me.payAmount, me.retAmount)).then(function() {
        var boneId = me.payDetails["boneId"];

        me.resetChangeBonMoney();

        var text = POS.lang.getText("MESSAGES", "NEW_AMOUNT_SUCCESFULLY_CHANGED");
        POS.message.show(text, "warning", "OK");

        var bonId = me.BonManager.currentBonId;
        me.BonManager.reset();
        $.when(me.BonManager.loadFromId(bonId, true)).then(function() {
            me.components.inputField.input.properties.attributes.value = "";
            me.components.inputField.input.reAttribute();

            /*privMan.enablePrivilege (privList);*/

            $.when(Trans.updateLastExportedBONEId(boneId)).done(function() {
                defret.resolve();
            });
        });
    });
    return defret.promise();
}

legacy.resetChangeBonMoney = function() {
    this.payDetails = null;
    this.payAmount = null;
    this.retAmount = null;
}



// Legacy-Konstruktor zum Uebertragen (addclass/htmlType/rootStyles/Felder):
// function CPosReceiptList(id) {
//     this.construct(id,"CPosReceiptList");
// 
//     this.htmlType = "div";
// 
//     this.properties = {
//         styles: {
//             position: "absolute",
//             width: "100%",
//             height: "100%",
//             left: "0px",
//             top: "0px",
//             zIndex: "1",
//             backgroundColor: "#F8F8F8"
//         }
//     }
// 
//     this.mode = null;
//     this.bonsPerScreen = 15;
//     this.scrollScreen = 0;
//     this.searchParameters = null;
//     this.bestBon = null;
//     this.bestTimestamp = null;
//     this.listedBons = new Array(); // Only used in case "parked"
//     this.allBons = 0;
//     this.Display = new CDisplay();
//     this.Main = null;
//     this.showPaydown = false;
//     this.customerId = null;
//     this.bonForCustomer = null;
// }
export const CPosReceiptList = defineReactScreen({
  className: 'CPosReceiptList',
  addclass: 'CPosReceiptList',
  htmlType: 'div',
  Screen: CPosReceiptListScreen,
  rootStyles: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '0px',
    top: '0px',
    zIndex: '1',
    backgroundColor: '#F8F8F8',
  },
  // WICHTIG: diese Felder gehoeren in den Konstruktor, nicht in prepare().
  // CMainPOS setzt direkt nach addChild() Main, mode und showPaydown und
  // ruft danach buildArrayBons() - alles VOR draw(), wo prepare erst liefe.
  fields: () => ({
    mode: null,
    bonsPerScreen: 15,
    scrollScreen: 0,
    searchParameters: null,
    bestBon: null,
    bestTimestamp: null,
    listedBons: new Array(), // Only used in case "parked"
    allBons: 0,
    Display: new CDisplay(),
    showPaydown: false,
    customerId: null,
    bonForCustomer: null,
  }),
  // Das Legacy-prepare laeuft VOR create(): es holt die Bons aus der DB und
  // baut in der Kopien-Ansicht den CBonManager - beides braucht React schon
  // beim ersten Rendern. Rueckgabe ist ein Deferred, die Bridge wartet.
  prepare(bridge) {
    return legacy.prepare.call(bridge);
  },
  // Nach dem Mount nur noch VERDRAHTUNG: die Namen zeigen jetzt auf die
  // echten Handles, und das Eingabefeld bekommt seinen .input - darueber
  // greifen CEANManager (Kundenkarte) und CMainPOS (Fokus) von aussen zu.
  mountLegacy(bridge) {
    bridge.nameComponent('0', 'titleContainer');
    bridge.nameComponent('1', 'listContainer');
    bridge.nameComponent('1:0', 'list');
    bridge.nameComponent('2', 'scrollBack');
    bridge.nameComponent('3', 'scrollUp');
    bridge.nameComponent('4', 'scrollDown');
    bridge.nameComponent('5', 'posysseus');
    bridge.nameComponent('6', 'moneyButton');
    bridge.nameComponent('7', 'searchButton');
    bridge.nameComponent('8', 'resetButton');
    bridge.nameComponent('9', 'functionButtons');
    bridge.nameComponent('10', 'numPad');
    bridge.nameComponent('11', 'inputField');
    bridge.nameComponent('12', 'bonContainer');
    bridge.nameComponent('12:0', 'bon');
    bridge.nameComponent('12:1', 'bonSum');
    bridge.nameComponent('13', 'scrollBon');
    bridge.nameComponent('13:0', 'scrollUpBon');
    bridge.nameComponent('13:1', 'scrollDownBon');
    bridge.nameComponent('14', 'suminfo');

    verdrahteInputField(bridge, '11');

    // Legacy-Code ruft auf diesen Rahmen reDraw() (CBonSearch nach einer
    // neuen Suche, reDrawList nach dem Blaettern). Der Legacy-reDraw wuerde
    // das Element ueber den Builder NEU bauen - bei einem React-Knoten waere
    // React danach nicht mehr synchron. Deshalb: reDraw heisst hier
    // "neu rendern".
    reDrawUeberReact(bridge, '1');
  },
  legacyMethods: legacy,
});
